import React, { useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import Card from "../components/ui/Card";
import Tabs from "../components/ui/Tabs";
import Button from "../components/ui/Button";
import Icon from "../components/ui/Icon";
import Modal from "../components/ui/Modal";
import AppLoader from "../components/ui/AppLoader";
import Switch from "../components/ui/Switch";
import PreviewBox from "../components/ui/PreviewBox";
import SliderDisplay from "../components/ui/SliderDisplay";
import { InputField, SelectField, TextAreaField } from "../components/ui/Field";
import { generateAgentTonePreview } from "../api/agent-tone-control-api";
import {
  getOrganizationBehavior,
  updateOrganizationBehavior,
  updateOrganizationBehaviorMetaData,
} from "../api/organization-behavior-prompt-Api";
import {
  AGENT_TONE_SELECT_OPTIONS,
  AGENT_TONE_TOGGLE_FIELDS,
  TONE_SLIDERS,
} from "../utils/agentTone/config";
import {
  cloneDefaultAgentToneState,
  createGeneratePayload,
  createRequestId,
  getEnabledToggleCount,
  getToneAverage,
} from "../utils/agentTone/payload";
import {
  appendAgentToneHistory,
  loadAgentToneDraft,
  saveAgentToneDraft,
} from "../utils/agentTone/storage";
import { getOrgId } from "../utils/authHelpers";

const TONE_TAB_ITEMS = [
  { key: "tone", label: "1. Tone" },
  { key: "voiceFlow", label: "2. Voice & Flow" },
  { key: "behavior", label: "3. Behavior" },
  { key: "context", label: "4. Context & Output" },
];
const DEFAULT_ORG_ID = "+61340541111";

function toSelectOptions(options = []) {
  return options.map((value) => ({ value, label: value }));
}

function createInitialFormState() {
  const defaults = cloneDefaultAgentToneState();
  const savedDraft = loadAgentToneDraft(); 

  if (!savedDraft) return defaults;

  return {
    ...defaults,
    ...savedDraft,
    tone: {
      ...defaults.tone,
      ...(savedDraft.tone || {}),
    },
    toggles: {
      ...defaults.toggles,
      ...(savedDraft.toggles || {}),
    },
    output: {
      ...defaults.output,
      ...(savedDraft.output || {}),
    },
  };
}

function normalizeConfigurationName(name = "") {
  return String(name).trim().toLowerCase();
}

function getConfigurationSortTime(configuration = {}) {
  const rawTime =
    configuration?.latest_output?.saved_at ||
    configuration?.updated_at ||
    configuration?.created_at ||
    "";
  const parsed = new Date(rawTime).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function buildAgentToneControls(formState) {
  return {
    mode: formState.mode,
    tone: formState.tone,
    personality_type: formState.personality_type,
    brand_voice: formState.brand_voice,
    communication_style: formState.communication_style,
    greeting_style: formState.greeting_style,
    question_asking_style: formState.question_asking_style,
    closing_style: formState.closing_style,
    error_handling_style: formState.error_handling_style,
    apology_level: formState.apology_level,
    hedging_level: formState.hedging_level,
    instruction_vs_explanation_preference:
      formState.instruction_vs_explanation_preference,
    user_expertise_level: formState.user_expertise_level,
    industry_domain: formState.industry_domain,
    use_case_type: formState.use_case_type,
    toggles: formState.toggles,
    output: formState.output,
    custom_instructions: formState.custom_instructions || "",
    request_id: formState.request_id,
  };
}

function createFormStateFromControls(savedControls = {}, fallbackInstructions = "") {
  const defaults = cloneDefaultAgentToneState();
  return {
    ...defaults,
    ...savedControls,
    tone: {
      ...defaults.tone,
      ...(savedControls.tone || {}),
    },
    toggles: {
      ...defaults.toggles,
      ...(savedControls.toggles || {}),
    },
    output: {
      ...defaults.output,
      ...(savedControls.output || {}),
    },
    custom_instructions:
      savedControls.custom_instructions ??
      fallbackInstructions ??
      defaults.custom_instructions,
  };
}

export function AgentToneControlPage({ setCurrentPage, notify }) {
  const [activeTab, setActiveTab] = useState("tone");
  const [formState, setFormState] = useState(createInitialFormState);
  const [loadingBehavior, setLoadingBehavior] = useState(true);
  const [savingBehavior, setSavingBehavior] = useState(false);
  const [existingMetaData, setExistingMetaData] = useState({});
  const [, setCurrentBehaviorText] = useState("");
  const [savedConfigurations, setSavedConfigurations] = useState([]);
  const [selectedConfigurationName, setSelectedConfigurationName] = useState("");
  const [newConfigurationName, setNewConfigurationName] = useState("");
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [showInitialSelection, setShowInitialSelection] = useState(true);
  const [isNewConfigurationFlow, setIsNewConfigurationFlow] = useState(false);
  const [previewState, setPreviewState] = useState({
    loading: false,
    error: "",
    data: null,
    lastPayload: null,
    lastUpdatedAt: null,
  });
  const organizationId = getOrgId() || DEFAULT_ORG_ID;

  useEffect(() => {
    setCurrentPage("/agent-tone-control");
  }, [setCurrentPage]);

  useEffect(() => {
    const syncBehavior = async () => {
      if (!organizationId) {
        setLoadingBehavior(false);
        return;
      }

      try {
        setLoadingBehavior(true);
        const response = await getOrganizationBehavior(organizationId);
        setCurrentBehaviorText(
          response?.behavior ||
            response?.behavior_prompt ||
            response?.prompt ||
            "",
        );

        const metaData = response?.meta_data || {};
        setExistingMetaData(metaData);

        const persistedConfigurations = Array.isArray(
          metaData?.agent_tone_saved_configurations,
        )
          ? metaData.agent_tone_saved_configurations
          : [];
        const persistedCurrentConfigurationName =
          metaData?.agent_tone_current_configuration_name ||
          persistedConfigurations.find((item) => item?.is_current)?.name ||
          "";
        setSavedConfigurations(persistedConfigurations);
        setSelectedConfigurationName(persistedCurrentConfigurationName);
        setNewConfigurationName("");
        setShowInitialSelection(true);
        setIsNewConfigurationFlow(false);

        const currentConfiguration = persistedConfigurations.find(
          (item) =>
            normalizeConfigurationName(item?.name) ===
            normalizeConfigurationName(persistedCurrentConfigurationName),
        );

        const savedControls =
          metaData?.agent_tone_controls ||
          metaData?.controls ||
          currentConfiguration?.controls ||
          null;
        const savedResult =
          metaData?.agent_tone_latest_output ||
          metaData?.agent_tone_last_result ||
          currentConfiguration?.latest_output ||
          metaData?.last_result ||
          null;

        if (savedControls) {
          setFormState(
            createFormStateFromControls(
              savedControls,
              response?.additional_instructions || "",
            ),
          );
        } else if (response?.additional_instructions) {
          setFormState((prev) => ({
            ...prev,
            custom_instructions: response.additional_instructions,
          }));
        }

        if (savedResult) {
          setPreviewState((prev) => ({
            ...prev,
            data: savedResult,
            error: "",
            lastUpdatedAt: response?.updated_at || prev.lastUpdatedAt,
          }));
        }
      } catch (error) {
        // Graceful fallback to local draft/defaults.
      } finally {
        setLoadingBehavior(false);
      }
    };

    syncBehavior();
  }, [organizationId]);

  useEffect(() => {
    saveAgentToneDraft(formState);
  }, [formState]);

  const toneAverage = useMemo(() => getToneAverage(formState.tone), [formState.tone]);
  const enabledToggles = useMemo(
    () => getEnabledToggleCount(formState.toggles),
    [formState.toggles],
  );
  const selectedConfigurationExists = useMemo(
    () =>
      (savedConfigurations || []).some(
        (item) =>
          normalizeConfigurationName(item?.name) ===
          normalizeConfigurationName(selectedConfigurationName),
      ),
    [savedConfigurations, selectedConfigurationName],
  );
  const sortedSavedConfigurations = useMemo(
    () =>
      [...(savedConfigurations || [])].sort(
        (a, b) => getConfigurationSortTime(b) - getConfigurationSortTime(a),
      ),
    [savedConfigurations],
  );

  const updateField = (key, value) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateToneField = (key, value) => {
    setFormState((prev) => ({
      ...prev,
      tone: {
        ...prev.tone,
        [key]: Number(value),
      },
    }));
  };

  const updateToggle = (key, value) => {
    setFormState((prev) => ({
      ...prev,
      toggles: {
        ...prev.toggles,
        [key]: value,
      },
    }));
  };

  const updateOutputField = (key, value) => {
    setFormState((prev) => ({
      ...prev,
      output: {
        ...prev.output,
        [key]: value,
      },
    }));
  };

  const handleReset = () => {
    const resetState = cloneDefaultAgentToneState();
    resetState.request_id = createRequestId("demo");
    setFormState(resetState);
    setPreviewState((prev) => ({
      ...prev,
      error: "",
      data: null,
      lastPayload: null,
      lastUpdatedAt: null,
    }));

    notify?.({
      tone: "info",
      title: "Agent tone reset",
      description: "Controls restored to default profile.",
    });
  };

  const handleGeneratePreview = async () => {
    const payload = createGeneratePayload(formState);

    setPreviewState((prev) => ({
      ...prev,
      loading: true,
      error: "",
      lastPayload: payload,
    }));

    try {
      const response = await generateAgentTonePreview(payload);
      const historyRecord = {
        id: createRequestId("run"),
        created_at: new Date().toISOString(),
        request_id: payload.request_id,
        mode: payload.mode,
        status: "success",
        controls: payload,
        output: response,
      };

      setPreviewState((prev) => ({
        ...prev,
        loading: false,
        data: response,
        lastUpdatedAt: new Date().toISOString(),
      }));
      appendAgentToneHistory(historyRecord);

      notify?.({
        tone: "success",
        title: "Preview generated",
        description: "Agent prompt and sample response have been refreshed.",
      });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to generate preview.";
      const historyRecord = {
        id: createRequestId("run"),
        created_at: new Date().toISOString(),
        request_id: payload.request_id,
        mode: payload.mode,
        status: "failed",
        controls: payload,
        output: null,
        error: message,
      };

      setPreviewState((prev) => ({
        ...prev,
        loading: false,
        error: message,
      }));
      appendAgentToneHistory(historyRecord);

      notify?.({
        tone: "danger",
        title: "Preview failed",
        description: message,
      });
    }
  };

  const handleOpenSaveModal = () => {
    if (!organizationId) {
      notify?.({
        tone: "danger",
        title: "Missing organization",
        description: "Organization ID was not found.",
      });
      return;
    }

    if (!previewState.data?.prompt) {
      notify?.({
        tone: "warning",
        title: "Generate preview first",
        description: "Behavior requires the generated prompt from preview.",
      });
      return;
    }

    if (!selectedConfigurationName) {
      notify?.({
        tone: "warning",
        title: "Select saved configuration",
        description: "Choose a saved configuration first to overwrite it.",
      });
      return;
    }

    setSaveModalOpen(true);
  };

  const handleBackToSelectionTable = () => {
    setSaveModalOpen(false);
    setShowInitialSelection(true);
  };

  const buildLatestOutputPayload = () => ({
    request_id: previewState.data.request_id || formState.request_id,
    mode: previewState.data.mode || formState.mode,
    prompt_version: previewState.data.prompt_version || null,
    provider: previewState.data.provider || null,
    cached: Boolean(previewState.data.cached),
    sample_user_message: previewState.data.sample_user_message || "",
    sample_agent_response: previewState.data.sample_agent_response || "",
    prompt: previewState.data.prompt || "",
    saved_at: new Date().toISOString(),
  });

  const handleSaveCurrentBehavior = async () => {
    if (!organizationId) {
      notify?.({
        tone: "danger",
        title: "Missing organization",
        description: "Organization ID was not found.",
      });
      return;
    }

    if (!previewState.data?.prompt) {
      notify?.({
        tone: "warning",
        title: "Generate preview first",
        description: "Behavior requires the generated prompt from preview.",
      });
      return;
    }

    const latestOutput = buildLatestOutputPayload();
    const controlsSnapshot = buildAgentToneControls(formState);
    const behaviorPayload = {
      behavior: previewState.data.prompt,
      additional_instructions: formState.custom_instructions || "",
    };
    const normalizedSelectedName = normalizeConfigurationName(selectedConfigurationName);
    const hasExistingSelectedConfig = (savedConfigurations || []).some(
      (item) => normalizeConfigurationName(item?.name) === normalizedSelectedName,
    );
    const nextSavedConfigurations =
      selectedConfigurationName && !hasExistingSelectedConfig
        ? [
            ...(savedConfigurations || []).map((item) => ({
              ...item,
              is_current: false,
            })),
            {
              name: selectedConfigurationName,
              controls: controlsSnapshot,
              latest_output: latestOutput,
              behavior: previewState.data.prompt,
              is_current: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
          ]
        : (savedConfigurations || []).map((item) => ({
            ...item,
            behavior:
              normalizeConfigurationName(item?.name) === normalizedSelectedName
                ? previewState.data.prompt
                : item?.behavior,
            is_current:
              normalizeConfigurationName(item?.name) === normalizedSelectedName &&
              Boolean(selectedConfigurationName),
          }));
    const metaDataPayload = {
      ...existingMetaData,
      agent_tone_controls: controlsSnapshot,
      agent_tone_latest_output: latestOutput,
      agent_tone_saved_configurations: nextSavedConfigurations,
      agent_tone_current_configuration_name: selectedConfigurationName || "",
    };

    try {
      setSavingBehavior(true);
      await updateOrganizationBehavior(organizationId, behaviorPayload);
      await updateOrganizationBehaviorMetaData(organizationId, metaDataPayload);
      setExistingMetaData(metaDataPayload);
      setCurrentBehaviorText(behaviorPayload.behavior);
      setSavedConfigurations(nextSavedConfigurations);
      setIsNewConfigurationFlow(false);
      setShowInitialSelection(true);
      notify?.({
        tone: "success",
        title: "Behavior saved",
        description: "Current behavior and metadata were updated successfully.",
      });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save behavior.";
      notify?.({
        tone: "danger",
        title: "Save failed",
        description: message,
      });
    } finally {
      setSavingBehavior(false);
    }
  };

  const handleOverwriteSavedConfiguration = async () => {
    const latestOutput = {
      ...buildLatestOutputPayload(),
    };

    const controlsSnapshot = buildAgentToneControls(formState);
    const nowIso = new Date().toISOString();
    const nextSavedConfigurations = (savedConfigurations || []).map((config) => {
      if (
        normalizeConfigurationName(config?.name) ===
        normalizeConfigurationName(selectedConfigurationName)
      ) {
        return {
          ...config,
          controls: controlsSnapshot,
          latest_output: latestOutput,
          behavior: previewState.data.prompt,
          is_current: true,
          updated_at: nowIso,
        };
      }

      return {
        ...config,
        is_current: false,
      };
    });

    const behaviorPayload = {
      behavior: previewState.data.prompt,
      additional_instructions: formState.custom_instructions || "",
    };
    const metaDataPayload = {
      ...existingMetaData,
      agent_tone_controls: controlsSnapshot,
      agent_tone_latest_output: latestOutput,
      agent_tone_saved_configurations: nextSavedConfigurations,
      agent_tone_current_configuration_name: selectedConfigurationName,
    };

    try {
      setSavingBehavior(true);
      await updateOrganizationBehavior(organizationId, behaviorPayload);
      await updateOrganizationBehaviorMetaData(organizationId, metaDataPayload);
      setExistingMetaData(metaDataPayload);
      setCurrentBehaviorText(behaviorPayload.behavior);
      setSavedConfigurations(nextSavedConfigurations);
      setSaveModalOpen(false);
      setIsNewConfigurationFlow(false);
      setShowInitialSelection(true);
      notify?.({
        tone: "success",
        title: "Configuration overwritten",
        description: "Saved configuration and current behavior were updated.",
      });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save behavior.";
      notify?.({
        tone: "danger",
        title: "Save failed",
        description: message,
      });
    } finally {
      setSavingBehavior(false);
    }
  };

  const applySavedConfigurationByName = (configurationName) => {
    const selectedName = configurationName || "";
    setSelectedConfigurationName(selectedName);
    if (!selectedName) return;

    const selectedConfiguration = (savedConfigurations || []).find(
      (item) =>
        normalizeConfigurationName(item?.name) === normalizeConfigurationName(selectedName),
    );
    if (!selectedConfiguration) return;

    const savedControls = selectedConfiguration.controls || {};
    setFormState(
      createFormStateFromControls(
        savedControls,
        savedControls.custom_instructions || formState.custom_instructions,
      ),
    );

    const savedOutput = selectedConfiguration.latest_output;
    if (savedOutput) {
      setPreviewState((prev) => ({
        ...prev,
        data: savedOutput,
        error: "",
        lastUpdatedAt: savedOutput.saved_at || prev.lastUpdatedAt,
      }));
    }
  };

  const handleStartNewConfiguration = () => {
    const trimmedName = String(newConfigurationName || "").trim();
    if (!trimmedName) {
      notify?.({
        tone: "warning",
        title: "Configuration name required",
        description: "Enter a name before creating a new configuration.",
      });
      return;
    }

    const resetState = cloneDefaultAgentToneState();
    resetState.request_id = createRequestId("new");
    setFormState(resetState);
    setPreviewState({
      loading: false,
      error: "",
      data: null,
      lastPayload: null,
      lastUpdatedAt: null,
    });
    setSelectedConfigurationName(trimmedName);
    setIsNewConfigurationFlow(true);
    setActiveTab("tone");
    setShowInitialSelection(false);
  };

  const handleEditSavedConfiguration = (configurationName) => {
    applySavedConfigurationByName(configurationName);
    setIsNewConfigurationFlow(false);
    setShowInitialSelection(false);
  };

  const handleSetCurrentConfiguration = async (configurationName) => {
    const targetConfiguration = (savedConfigurations || []).find(
      (item) =>
        normalizeConfigurationName(item?.name) === normalizeConfigurationName(configurationName),
    );
    if (!targetConfiguration) return;

    const updatedConfigurations = (savedConfigurations || []).map((item) => ({
      ...item,
      is_current:
        normalizeConfigurationName(item?.name) ===
        normalizeConfigurationName(configurationName),
    }));

    const controlsSnapshot = targetConfiguration.controls || {};
    const latestOutput = targetConfiguration.latest_output || null;
    const behaviorText =
      targetConfiguration?.behavior ||
      targetConfiguration?.behavior_prompt ||
      "";
    if (!String(behaviorText || "").trim()) {
      notify?.({
        tone: "warning",
        title: "Stored behavior missing",
        description:
          "Selected configuration has no stored behavior in metadata. Edit and save it first.",
      });
      return;
    }
    const additionalInstructions =
      controlsSnapshot?.custom_instructions || formState.custom_instructions || "";
    const behaviorPayload = {
      behavior: behaviorText,
      additional_instructions: additionalInstructions,
    };
    const metaDataPayload = {
      ...existingMetaData,
      agent_tone_controls: controlsSnapshot,
      agent_tone_latest_output: latestOutput,
      agent_tone_saved_configurations: updatedConfigurations,
      agent_tone_current_configuration_name: targetConfiguration.name || "",
    };

    try {
      setSavingBehavior(true);
      await updateOrganizationBehavior(organizationId, behaviorPayload);
      setCurrentBehaviorText(behaviorPayload.behavior);
      await updateOrganizationBehaviorMetaData(organizationId, metaDataPayload);
      setExistingMetaData(metaDataPayload);
      setSavedConfigurations(updatedConfigurations);
      setSelectedConfigurationName(targetConfiguration.name || "");
      setShowInitialSelection(true);
      notify?.({
        tone: "success",
        title: "Current configuration updated",
        description: `${targetConfiguration.name} is now marked as current.`,
      });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update current configuration.";
      notify?.({
        tone: "danger",
        title: "Update failed",
        description: message,
      });
    } finally {
      setSavingBehavior(false);
    }
  };

  const formatSavedAt = (value) => {
    if (!value) return "N/A";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return parsed.toLocaleString();
  };

  const initialConfigTableColumns = [
    {
      title: "Configuration",
      dataIndex: "name",
      key: "name",
      width: 220,
      render: (_, record) => (
        <div className="agent-tone-config-name">
          <div className="agent-tone-config-name-text">{record?.name || "Unnamed"}</div>
          {record?.is_current ? (
            <span className="agent-tone-current-badge">Current</span>
          ) : null}
        </div>
      ),
    },
    {
      title: "Saved At",
      key: "saved_at",
      width: 190,
      render: (_, record) =>
        formatSavedAt(record?.latest_output?.saved_at || record?.updated_at),
    },
    {
      title: "Sample User Message",
      key: "sample_user_message",
      render: (_, record) => (
        <div className="agent-tone-sample-cell">
          {record?.latest_output?.sample_user_message || "No sample user message."}
        </div>
      ),
    },
    {
      title: "Sample Response",
      key: "sample_agent_response",
      render: (_, record) => (
        <div className="agent-tone-sample-cell">
          {record?.latest_output?.sample_agent_response || "No sample response."}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <div className="agent-tone-config-actions">
          <Button
            variant="secondary"
            onClick={() => handleSetCurrentConfiguration(record?.name)}
            loading={savingBehavior}
            disabled={!record?.name}
          >
            Set as Current
          </Button>
          <Button
            variant="primary"
            onClick={() => handleEditSavedConfiguration(record?.name)}
            disabled={!record?.name || savingBehavior}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ];

  const renderInitialSelectionScreen = () => (
    <Card
      title="Choose Starting Point"
      subtitle="Pick a saved configuration from previous data, mark it current, or open one for editing."
      className="agent-tone-card agent-tone-initial-card"
    >
      <div className="agent-tone-initial-toolbar">
        <InputField
          label="New Configuration Name"
          value={newConfigurationName}
          onChange={(event) => setNewConfigurationName(event.target.value)}
          placeholder="Enter new settings name"
        />
        <Button
          variant="primary"
          onClick={handleStartNewConfiguration}
          disabled={savingBehavior}
        >
          Create New Config
        </Button>
      </div>

      <div className="agent-tone-config-table-wrap">
        <Table
          className="agent-tone-config-table"
          columns={initialConfigTableColumns}
          dataSource={sortedSavedConfigurations}
          rowKey={(record) =>
            record?.name || `${record?.updated_at || ""}-${record?.created_at || ""}`
          }
          pagination={{
            pageSize: 5,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
          scroll={{ x: 980 }}
          locale={{ emptyText: "No saved configurations found in previous data." }}
        />
      </div>
    </Card>
  );

  const renderToneTab = () => (
    <Card
      title="Tone & Style Controls"
      subtitle="Fine-tune response characteristics with weighted sliders."
      className="agent-tone-card"
    >
      <div className="slider-group">
        {TONE_SLIDERS.map((item) => (
          <SliderDisplay
            key={item.key}
            label={item.label}
            value={formState.tone[item.key]}
            minLabel={item.minLabel}
            maxLabel={item.maxLabel}
            onChange={(value) => updateToneField(item.key, value)}
          />
        ))}
      </div>
    </Card>
  );

  const renderVoiceTab = () => (
    <Card
      title="Voice & Conversation Flow"
      subtitle="Define persona, communication style, and how conversations open and close."
      className="agent-tone-card"
    >
      <div className="form-grid cols-2">
        <SelectField
          label="Personality Type"
          value={formState.personality_type}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.personality_type)}
          onChange={(event) => updateField("personality_type", event.target.value)}
        />
        <SelectField
          label="Brand Voice"
          value={formState.brand_voice}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.brand_voice)}
          onChange={(event) => updateField("brand_voice", event.target.value)}
        />
      </div>
      <div className="form-grid cols-3">
        <SelectField
          label="Communication Style"
          value={formState.communication_style}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.communication_style)}
          onChange={(event) => updateField("communication_style", event.target.value)}
        />
        <SelectField
          label="Greeting Style"
          value={formState.greeting_style}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.greeting_style)}
          onChange={(event) => updateField("greeting_style", event.target.value)}
        />
        <SelectField
          label="Question Asking Style"
          value={formState.question_asking_style}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.question_asking_style)}
          onChange={(event) => updateField("question_asking_style", event.target.value)}
        />
      </div>
      <div className="form-grid cols-2">
        <SelectField
          label="Closing Style"
          value={formState.closing_style}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.closing_style)}
          onChange={(event) => updateField("closing_style", event.target.value)}
        />
      </div>
    </Card>
  );

  const renderBehaviorTab = () => (
    <Card
      title="Behavior & Handling Preferences"
      subtitle="Define error handling profile, caution level, and response behavior toggles."
      className="agent-tone-card"
    >
      <div className="form-grid cols-2">
        <SelectField
          label="Mode"
          value={formState.mode}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.mode)}
          onChange={(event) => updateField("mode", event.target.value)}
        />
        <SelectField
          label="Error Handling Style"
          value={formState.error_handling_style}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.error_handling_style)}
          onChange={(event) => updateField("error_handling_style", event.target.value)}
        />
        <SelectField
          label="Apology Level"
          value={formState.apology_level}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.apology_level)}
          onChange={(event) => updateField("apology_level", event.target.value)}
        />
      </div>

      <div className="form-grid cols-2">
        <SelectField
          label="Hedging Level"
          value={formState.hedging_level}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.hedging_level)}
          onChange={(event) => updateField("hedging_level", event.target.value)}
        />
        <SelectField
          label="Instruction vs Explanation"
          value={formState.instruction_vs_explanation_preference}
          options={toSelectOptions(
            AGENT_TONE_SELECT_OPTIONS.instruction_vs_explanation_preference,
          )}
          onChange={(event) =>
            updateField("instruction_vs_explanation_preference", event.target.value)
          }
        />
      </div>

      <div className="agent-tone-toggle-list">
        {AGENT_TONE_TOGGLE_FIELDS.map((item) => (
          <div className="toggle-row" key={item.key}>
            <div className="toggle-meta">
              <div className="toggle-label">{item.label}</div>
              <div className="toggle-description">{item.description}</div>
            </div>
            <Switch
              checked={Boolean(formState.toggles[item.key])}
              label={item.label}
              onChange={(next) => updateToggle(item.key, next)}
            />
          </div>
        ))}
      </div>
    </Card>
  );

  const renderContextTab = () => (
    <Card
      title="User Context & Output"
      subtitle="Configure audience profile, use-case domain, and response formatting constraints."
      className="agent-tone-card"
    >
      <div className="form-grid cols-2">
        <InputField
          label="Request ID"
          value={formState.request_id}
          onChange={(event) => updateField("request_id", event.target.value)}
        />
      </div>

      <div className="form-grid cols-3">
        <SelectField
          label="User Expertise Level"
          value={formState.user_expertise_level}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.user_expertise_level)}
          onChange={(event) => updateField("user_expertise_level", event.target.value)}
        />
        <SelectField
          label="Industry Domain"
          value={formState.industry_domain}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.industry_domain)}
          onChange={(event) => updateField("industry_domain", event.target.value)}
        />
        <SelectField
          label="Use Case Type"
          value={formState.use_case_type}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.use_case_type)}
          onChange={(event) => updateField("use_case_type", event.target.value)}
        />
      </div>

      <div className="form-grid cols-3">
        <SelectField
          label="Output Language"
          value={formState.output.output_language}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.output_language)}
          onChange={(event) => updateOutputField("output_language", event.target.value)}
        />
        <SelectField
          label="Localization Region"
          value={formState.output.localization_region}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.localization_region)}
          onChange={(event) => updateOutputField("localization_region", event.target.value)}
        />
        <SelectField
          label="Formatting Style"
          value={formState.output.formatting_style}
          options={toSelectOptions(AGENT_TONE_SELECT_OPTIONS.formatting_style)}
          onChange={(event) => updateOutputField("formatting_style", event.target.value)}
        />
      </div>

      <TextAreaField
        label="Custom Instructions"
        value={formState.custom_instructions}
        rows={4}
        onChange={(event) => updateField("custom_instructions", event.target.value)}
      />
    </Card>
  );

  const previewResponse = previewState.data;
  const renderPreviewPanel = () => (
    <Card
      title="Live Preview"
      subtitle="Generate and inspect prompt output from current control values."
      className="agent-tone-preview-card"
    >
      <div className="agent-tone-kpis">
        <div className="agent-tone-kpi">
          <div className="agent-tone-kpi-label">Tone Average</div>
          <div className="agent-tone-kpi-value">{toneAverage}%</div>
        </div>
        <div className="agent-tone-kpi">
          <div className="agent-tone-kpi-label">Enabled Toggles</div>
          <div className="agent-tone-kpi-value">
            {enabledToggles}/{AGENT_TONE_TOGGLE_FIELDS.length}
          </div>
        </div>
      </div>

      {previewState.error ? (
        <div className="agent-tone-preview-error">{previewState.error}</div>
      ) : null}

      {previewResponse ? (
        <div className="agent-tone-preview-stack">
          <div className="agent-tone-conversation">
            <div className="agent-tone-message agent-tone-message-user">
              <div className="agent-tone-message-title">Sample User Message</div>
              <div>{previewResponse.sample_user_message || "No sample user message."}</div>
            </div>
            <div className="agent-tone-message agent-tone-message-agent">
              <div className="agent-tone-message-title">Sample Agent Response</div>
              <div>{previewResponse.sample_agent_response || "No sample response."}</div>
            </div>
          </div>
        </div>
      ) : (
        <PreviewBox title="Preview Status">
          Click "Generate Preview" to render a sample conversation from your current controls.
        </PreviewBox>
      )}

      {previewState.lastUpdatedAt ? (
        <div className="agent-tone-updated-at">
          Last updated: {new Date(previewState.lastUpdatedAt).toLocaleString()}
        </div>
      ) : null}
    </Card>
  );

  const renderActiveTab = () => {
    if (activeTab === "voiceFlow") return renderVoiceTab();
    if (activeTab === "behavior") return renderBehaviorTab();
    if (activeTab === "context") return renderContextTab();
    return renderToneTab();
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Agent Tone Control</h1>
          <p className="page-subtitle">
            Configure prompt behavior with advanced controls and instant preview generation.
          </p>
        </div>
        {!showInitialSelection ? (
          <div className="page-actions">
            <Button
              variant="secondary"
              onClick={handleBackToSelectionTable}
              disabled={previewState.loading || savingBehavior}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handleReset}
              iconLeft={<Icon name="x" size={15} />}
              disabled={previewState.loading || savingBehavior}
            >
              Reset
            </Button>
            {previewState.data ? (
              <Button
                variant="secondary"
                onClick={handleSaveCurrentBehavior}
                iconLeft={<Icon name="save" size={15} />}
                loading={savingBehavior}
                disabled={previewState.loading}
              >
                Save Behavior
              </Button>
            ) : null}
            {previewState.data &&
            selectedConfigurationName &&
            selectedConfigurationExists &&
            !isNewConfigurationFlow ? (
              <Button
                variant="secondary"
                onClick={handleOpenSaveModal}
                iconLeft={<Icon name="save" size={15} />}
                loading={savingBehavior}
                disabled={previewState.loading}
              >
                Overwrite Saved Config
              </Button>
            ) : null}
            <Button
              variant="primary"
              onClick={handleGeneratePreview}
              iconLeft={<Icon name="sparkles" size={15} />}
              loading={previewState.loading}
              disabled={savingBehavior}
            >
              Generate Preview
            </Button>
          </div>
        ) : null}
      </div>

      {loadingBehavior || previewState.loading ? (
        <AppLoader
          variant="page"
          label={loadingBehavior ? "Loading behavior" : "Generating preview"}
          sublabel={
            loadingBehavior
              ? "Populating saved controls and previous results..."
              : "Applying tone controls and building your sample response..."
          }
        />
      ) : showInitialSelection ? (
        renderInitialSelectionScreen()
      ) : (
        <div className="agent-tone-layout">
          <div className="agent-tone-editor">
            <Card className="agent-tone-tabs-shell">
              {selectedConfigurationName ? (
                <div className="agent-tone-selected-configuration-title">
                  Editing Saved Configuration: {selectedConfigurationName}
                </div>
              ) : null}
              <Tabs tabs={TONE_TAB_ITEMS} activeKey={activeTab} onChange={setActiveTab} />
              {renderActiveTab()}
            </Card>
          </div>
          <div className="agent-tone-preview">{renderPreviewPanel()}</div>
        </div>
      )}

      <Modal
        open={saveModalOpen}
        title="Overwrite Saved Configuration"
        onClose={() => setSaveModalOpen(false)}
        actions={[
          <Button
            key="cancel-save-config"
            variant="secondary"
            onClick={handleBackToSelectionTable}
            disabled={savingBehavior}
          >
            Cancel
          </Button>,
          <Button
            key="confirm-save-config"
            variant="primary"
            onClick={handleOverwriteSavedConfiguration}
            loading={savingBehavior}
            disabled={previewState.loading}
          >
            Overwrite
          </Button>,
        ]}
      >
        <p>
          This will overwrite the saved configuration{" "}
          <strong>{selectedConfigurationName || "selected configuration"}</strong>.
        </p>
      </Modal>
    </div>
  );
}

export default AgentToneControlPage;
